<template>
  <div class="d-flex flex-column flex-grow-1 h-100">
    <div class="d-flex align-items-center justify-content-between flex-wrap">
      <label class="text-white mb-0">Venue pin</label>
      <small v-if="pendingReset && !previewing" class="text-muted">
        Resets to the address when you save · <a href="#" @click.prevent="pendingReset = false">Undo</a>
      </small>
      <small v-else-if="movedByHand && hasAddress" class="text-muted">
        Moved by hand · <a href="#" @click.prevent="markReset">Reset to address</a>
      </small>
    </div>

    <template v-if="showMap">
      <info-hint v-if="!previewing && status === 'unresolved' && !pendingPin" variant="warning" icon="icon-alert-circle-exc" class="mt-2">
        This address couldn't be located. Click the map at your venue to drop the pin.
      </info-hint>
      <location-map class="mt-2"
                    fill
                    :latitude="shownLat"
                    :longitude="shownLng"
                    :draggable="canEdit"
                    :place-on-click="canEdit"
                    :show-locate="canEdit"
                    @move="onMove"
                    @locate-error="locateError = $event" />
    </template>
    <info-hint v-else-if="status === 'no_address'" variant="neutral" icon="icon-pin" class="mt-2">
      Add the address to set the venue pin.
    </info-hint>
    <info-hint v-else-if="status === 'lookup_failed'" variant="warning" icon="icon-alert-circle-exc" class="mt-2">
      The venue pin couldn't be loaded. Reload the page to try again.
    </info-hint>

    <div class="d-flex flex-column flex-sm-row justify-content-between mt-1">
      <small v-if="showMap && canEdit" class="text-white">Drag the pin to your entrance.</small>
      <small class="ml-sm-auto">
        <a href="https://docs.aws.amazon.com/location/latest/developerguide/data-attribution.html" target="_blank" rel="noopener">Location data sources</a>
      </small>
    </div>

    <info-hint v-if="locateError" variant="danger" icon="icon-alert-circle-exc" class="mt-2">
      {{ locateError }}
    </info-hint>

    <info-hint v-if="addressChanged && !pendingReset && !previewPin" variant="warning" icon="icon-alert-circle-exc" class="mt-2">
      The address changed. Update the pin from the new address?
      <template #action>
        <base-button variant="outlined" tone="primary" size="sm" @click="markReset">Reset to address</base-button>
      </template>
    </info-hint>
  </div>
</template>

<script setup lang="ts">
// Venue pin for a group (OTC-2926). GET and PUT companies/{id}/groups/{grid}/location
// (get-group-location, update-group-location). The GET fills the pin from the
// group's address when the address changed, so the pin shows without a save.
// Moving the pin or resetting it only changes pending state; Group settings
// calls savePending() after it saves the address (Update location details).
// Picking a suggested address calls previewAddressPin(): the pin moves on the
// map only; the address save sets the real pin.
import { ref, computed, onMounted, defineAsyncComponent } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'

type PinStatus = '' | 'resolved' | 'unresolved' | 'no_address' | 'lookup_failed'
type Pin = { latitude: number; longitude: number }

const LocationMap = defineAsyncComponent(() => import(/* webpackChunkName: "maplibre" */ '@/components/LocationMap.vue'))

const props = defineProps({
  companyId: { type: String, required: true },
  groupId: { type: [String, Number], required: true },
  canEdit: { type: Boolean, default: false },
  hasAddress: { type: Boolean, default: false },
})

const latitude = ref<number | null>(null)
const longitude = ref<number | null>(null)
const locationSource = ref<string | null>(null)
const status = ref<PinStatus>('')
const addressChanged = ref(false)

const pendingPin = ref<Pin | null>(null)
const pendingReset = ref(false)
const previewPin = ref<Pin | null>(null)
const locateError = ref('')

const url = computed(() => `${API_LOCATION}companies/${props.companyId}/groups/${props.groupId}/location`)
const shownPin = computed(() => pendingPin.value || previewPin.value)
const shownLat = computed(() => (shownPin.value ? shownPin.value.latitude : latitude.value))
const shownLng = computed(() => (shownPin.value ? shownPin.value.longitude : longitude.value))
// A picked address is on the map; its Undo would leave the preview showing
// a pin that won't save, so the reset line stays hidden.
const previewing = computed(() => previewPin.value !== null && pendingPin.value === null)
const movedByHand = computed(() => !pendingReset.value && (pendingPin.value !== null || locationSource.value === 'manual'))
const showMap = computed(() =>
  pendingPin.value !== null ||
  previewPin.value !== null ||
  (status.value === 'resolved' && latitude.value !== null && longitude.value !== null) ||
  status.value === 'unresolved')

async function authConfig() {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  return { headers: { Authorization: token } }
}

async function reload() {
  previewPin.value = null
  try {
    const response = await axios.get(url.value, await authConfig())
    const data = response.data || {}
    latitude.value = data.latitude === null || data.latitude === undefined ? null : Number(data.latitude)
    longitude.value = data.longitude === null || data.longitude === undefined ? null : Number(data.longitude)
    locationSource.value = data.location_source || null
    status.value = data.status || 'lookup_failed'
    addressChanged.value = data.address_changed === true
  } catch (err) {
    status.value = 'lookup_failed'
  }
}

function onMove(pin: Pin) {
  pendingPin.value = pin
  previewPin.value = null
  pendingReset.value = false
  locateError.value = ''
}

function markReset() {
  pendingReset.value = true
  pendingPin.value = null
}

// A picked address moves the pin preview. A pin moved by hand is marked to
// reset, so after the save it follows the new address.
function previewAddressPin(lat: number | null, lng: number | null) {
  if (lat === null || lng === null) return
  previewPin.value = { latitude: lat, longitude: lng }
  pendingPin.value = null
  pendingReset.value = locationSource.value === 'manual'
  locateError.value = ''
}

function hasPendingChange(): boolean {
  return pendingReset.value || pendingPin.value !== null
}

// Saves the pending pin change. Leaves it pending and rethrows on failure.
async function savePending() {
  if (pendingReset.value) {
    await axios.put(url.value, { reset_to_address: true }, await authConfig())
  } else if (pendingPin.value) {
    await axios.put(url.value, { latitude: pendingPin.value.latitude, longitude: pendingPin.value.longitude }, await authConfig())
  }
  pendingReset.value = false
  pendingPin.value = null
  previewPin.value = null
}

onMounted(reload)

defineExpose({ reload, hasPendingChange, savePending, previewAddressPin })
</script>
